import {
  type AcceptInvitationRequest,
  type AuthSession,
  type LoginRequest,
  type Role,
  roleAtLeast,
  type SignupRequest,
} from '@codot/contracts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authApi } from '../api/auth.api';
import { ApiError } from '../api/http';

type Status = 'unknown' | 'authenticated' | 'anonymous';

/**
 * Client-side mirror of the server session. It never holds tokens (they are HttpOnly cookies the
 * JS cannot read); it only knows WHO is signed in and WHICH org/role is active, for UI decisions.
 * The API re-checks everything on every request — this store is never the security boundary.
 */
export const useAuthStore = defineStore('auth', () => {
  const session = ref<AuthSession | null>(null);
  const status = ref<Status>('unknown');
  let bootstrapping: Promise<void> | null = null;

  const user = computed(() => session.value?.user ?? null);
  const activeOrganization = computed(() => session.value?.activeOrganization ?? null);
  const organizations = computed(() => session.value?.organizations ?? []);
  const role = computed<Role | null>(() => activeOrganization.value?.role ?? null);
  const isAuthenticated = computed(() => status.value === 'authenticated');

  /** `can('ADMIN')` → hide/disable controls the server would reject anyway. */
  const can = (minimum: Role): boolean => role.value !== null && roleAtLeast(role.value, minimum);

  /** Account Settings saved the profile (name, time zone, language, picture): show it everywhere without a reload. */
  function setUser(patch: Partial<AuthSession['user']>): void {
    if (session.value) session.value = { ...session.value, user: { ...session.value.user, ...patch } };
  }
  const setUserName = (name: string): void => setUser({ name });

  function apply(next: AuthSession | null): void {
    session.value = next;
    status.value = next ? 'authenticated' : 'anonymous';
  }

  /** Called once by the router guard; concurrent navigations share the same request. */
  function ensureLoaded(): Promise<void> {
    if (status.value !== 'unknown') return Promise.resolve();
    // The API may be restarting when the page opens: keep trying (about 30 s) instead of showing the sign-in page.
    bootstrapping ??= (async () => {
      for (const wait of [1_000, 2_000, 3_000, 5_000, 8_000, 10_000, 0]) {
        try {
          apply(await authApi.me());
          return;
        } catch (err: unknown) {
          if (err instanceof ApiError && err.status === 401) return apply(null);
          if (!wait || !(err instanceof ApiError && err.status >= 500)) throw err;
          await new Promise((r) => setTimeout(r, wait));
        }
      }
    })()
      .finally(() => {
        bootstrapping = null;
      });
    return bootstrapping;
  }

  /** Re-read the session (e.g. after a payment changes the plan or subscription status). */
  async function reload(): Promise<void> {
    apply(await authApi.me());
  }

  /** Signed in, or the challenge for the two-factor code (then login2fa). */
  async function login(body: LoginRequest): Promise<string | null> {
    const r = await authApi.login(body);
    if ('twoFactorRequired' in r) return r.challenge;
    apply(r);
    return null;
  }

  async function login2fa(challenge: string, code: string): Promise<void> {
    apply(await authApi.login2fa(challenge, code));
  }

  async function signup(body: SignupRequest): Promise<void> {
    apply(await authApi.signup(body));
  }

  async function acceptInvitation(body: AcceptInvitationRequest): Promise<void> {
    apply(await authApi.acceptInvitation(body));
  }

  async function switchOrganization(organizationId: string): Promise<void> {
    apply(await authApi.switchOrganization(organizationId));
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } finally {
      apply(null);
    }
  }

  /** The server said our session is gone (refresh failed). */
  function expire(): void {
    apply(null);
  }

  return {
    session,
    status,
    user,
    reload,
    activeOrganization,
    organizations,
    role,
    isAuthenticated,
    can,
    ensureLoaded,
    login,
    login2fa,
    signup,
    acceptInvitation,
    switchOrganization,
    logout,
    setUser,
    setUserName,
    expire,
  };
});
